<template>
    <div class="content-container">
        <div class='title'>{{$t('outils.compteur.step.two.title.representant.partenaire')}}</div>
        <div class='margin-top-8px'>{{$t('outils.compteur.step.one.two.subtitle.check')}}</div>

        <div class='input-container'>
            <div>{{$t('outils.compteur.step.two.civilite')}}</div>
            <DropDown
                :list="listCivilite"
                :selectedItem="selectedCivilite"
                :display="displayCivilite"
                @select="setCivilite"
                class="drop-down"
                :label="$t('outils.compteur.step.two.civilite')"/>
        </div>

        <div class='input-container'>
            <div>{{$t('outils.compteur.step.two.nom.representant')}}</div>
            <input class='text-input' v-model='nomRepresentant'>
        </div>

        <div class='input-container'>
            <div>{{$t('outils.compteur.step.two.prenom.representant')}}</div>
            <input class='text-input' v-model='prenomRepresentant'>
        </div>

        <div class='input-container'>
            <div>{{$t('outils.compteur.step.two.date.naissance.representant')}}</div>
            <DatePicker
                class="datepicker"
                @setDate="setDateNaissance"
                :dateValue="dateNaissanceRepresentant"
                :label="$t('outils.compteur.step.two.date.naissance.representant')"
            />
        </div>

        <div class='input-container'>
            <div>{{$t('outils.compteur.step.two.email.representant')}}</div>
            <input class='text-input' v-model='mailRepresentant' :class="{'no-valid': !isMailValid}">
        </div>

        <div class='input-container'>
            <div>
                {{$t('outils.compteur.step.two.adresse.representant')}}
                <ReglePastille cle="outils.etape2AdresseConditionnelle" />
            </div>
            <div @click="setAdresseIdentique(true)" class='margin-top-8px'>
                <RadioBox :selected='adresseIdentique' class='cursor-pointer'/>
                <span class='span-radio'>{{$t('outils.compteur.step.two.adresse.identique')}}</span>
            </div>
            <div @click="setAdresseIdentique(false)">
                <RadioBox :selected='!adresseIdentique' class='cursor-pointer'/>
                <span class='span-radio'>{{$t('outils.compteur.step.two.adresse.differente')}}</span>
            </div>
        </div>

        <div class='input-container' v-if='!adresseIdentique'>
            <div>{{$t('outils.compteur.step.two.adresse.postale')}}</div>
            <input class='text-input' v-model='adresseRepresentant'>
        </div>

        <div class='input-container' v-if='!adresseIdentique'>
            <div>{{$t('outils.compteur.step.two.code.postal')}}</div>
            <input class='text-input' v-model='codePostalRepresentant' type='number' @keypress="disableNegatif($event)" min='0'>
        </div>

        <div class='input-container' v-if='!adresseIdentique'>
            <div>{{$t('outils.compteur.step.two.ville')}}</div>
            <input class='text-input' v-model='villeRepresentant'>
        </div>

    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { COMPTEUR } from '@/modules/Outils/store/mutation-types';
import DropDown from '@/components/ui-components/DropDown.vue';
import { Civilite } from '@/modules/Outils/store/state';
import DatePicker from '@/components/ui-components/DatePicker.vue';
import regex from '@/common/utils/Regex';
import RadioBox from '@/components/ui-components/RadioBox.vue';
import ReglePastille from '@/components/regles/ReglePastille.vue';

export default defineComponent({
    emits: ['next'],
    components: { RadioBox, DropDown, DatePicker, ReglePastille },
    data() {
        return {
            listCivilite: [Civilite.monsieur, Civilite.madame],
        };
    },
    methods: {
        setCivilite(civilite: Civilite) {
            this.$store.commit(COMPTEUR.SET_CIVILITE, civilite);
        },
        displayCivilite(civilite: Civilite): string {
            return civilite;
        },
        setDateNaissance(dateNaissance: string) {
            this.$store.commit(COMPTEUR.SET_DATE_NAISSANCE_REPRESENTANT, dateNaissance);
        },
        setAdresseIdentique(isAdresseIdentique: boolean) {
            this.$store.commit(COMPTEUR.SET_ADRESSE_REPRESENTANT_IDENTIQUE, isAdresseIdentique);
        },
        disableNegatif(event: any): boolean{
            if(event.key == '-' || event.key == ',' || event.key == '.' || event.key == 'e'){
                event.preventDefault();
                return false;
            }else{
                return true;
            }
        }
    },
    computed: {
        isMailValid() : boolean {
            return this.mailRepresentant === '' || regex.mail().test(this.mailRepresentant);
        },
        selectedCivilite(): string {
            return this.civilite;
        },
        civilite: {
            get(): string {
                return this.$store.getters.getCivilite;
            },
            set(value: string) {
                this.$store.commit(COMPTEUR.SET_CIVILITE, value);
            },
        },
        nomRepresentant: {
            get(): string {
                return this.$store.getters.getNomRepresentant;
            },
            set(value: string) {
                this.$store.commit(COMPTEUR.SET_NOM_REPRESENTANT, value.trim());
            },
        },
        prenomRepresentant: {
            get(): string {
                return this.$store.getters.getPrenomRepresentant;
            },
            set(value: string) {
                this.$store.commit(COMPTEUR.SET_PRENOM_REPRESENTANT, value.trim());
            },
        },
        dateNaissanceRepresentant: {
            get(): string {
                return this.$store.getters.getDateNaissanceRepresentant;
            },
            set(value: string) {
                this.$store.commit(COMPTEUR.SET_DATE_NAISSANCE_REPRESENTANT, value);
            },
        },
        mailRepresentant: {
            get(): string {
                return this.$store.getters.getEmailRepresentant;
            },
            set(value: string) {
                this.$store.commit(COMPTEUR.SET_MAIL_REPRESENTANT, value.trim());
            },
        },
        adresseIdentique: {
            get(): boolean {
                return this.$store.getters.getAdresseIdentique;
            },
            set(value: boolean) {
                this.$store.commit(COMPTEUR.SET_ADRESSE_REPRESENTANT_IDENTIQUE, value);
            },
        },
        adresseRepresentant: {
            get(): string {
                return this.$store.getters.getAdressePostaleRepresentant;
            },
            set(value: string) {
                this.$store.commit(COMPTEUR.SET_ADRESSE_POSTALE_REPRESENTANT, value.trim());
            },
        },
        codePostalRepresentant: {
            get(): number | undefined {
                return this.$store.getters.getCodePostalRepresentant;
            },
            set(value: string) {
                this.$store.commit(COMPTEUR.SET_CODE_POSTAL_REPRESENTANT, value);
            },
        },
        villeRepresentant: {
            get(): string {
                return this.$store.getters.getVilleRepresentant;
            },
            set(value: string) {
                this.$store.commit(COMPTEUR.SET_VILLE_REPRESENTANT, value.trim());
            },
        },
    },

});
</script>

<style scoped>
.content-container {
    margin-top: 8px;
    padding: 16px 24px;
    overflow: auto;
    background-color: #fff;
    box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px rgba(0, 0, 0, 0.14), 0 1px 5px rgba(0, 0, 0, 0.12);
    border-radius: 6px;
}
.title {
    color: var(--dark-blue);
    font-weight: var(--font-weight-medium);
    font-size: var(--font-size-low-large);
}
.margin-top-8px {
    margin-top: 8px;
}
.input-container {
    margin-top: 16px;
}
.text-input {
    width: 30%;
    height: 35px;
    margin-top: 5px;
}
.drop-down {
    width: 30.5%;
    height: 45px;
    margin-top: 8px;
}
.datepicker{
    width: 30.5%;
    height: 45px;
    margin-top: 8px;
}
.no-valid {
    border: 1px solid var(--red) !important;
    border-radius: 4px;
}
.span-radio {
    vertical-align: super;
    cursor: pointer;
}
.cursor-pointer {
    cursor: pointer;
}
</style>
